import { useRef, useState } from 'react';
import { Body, Button, Field, I, Row } from '@leanin/ui';

import { getApiClient } from '@/lib/api-client';
import { failureFromError, throwApiError } from '@/lib/api-error';
import { uploadWithProgress } from '@/lib/upload';
import { useUpdateLearningTrack } from '@/lib/mutations/learning-tracks';
import styles from './TrackCoverUploadField.module.css';

// Explicit cover for a learning track — picked here, uploaded immediately
// (presign → PUT), then PATCHed onto the track. Unlike the resource cover
// field there's no deferred commit: the track always exists on this page.

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'] as const;
const MAX_BYTES = 5 * 1024 * 1024;

export function TrackCoverUploadField({
  trackId,
  currentCoverUrl,
}: {
  readonly trackId: string;
  readonly currentCoverUrl: string | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const updateTrack = useUpdateLearningTrack();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = () => inputRef.current?.click();

  const handleFile = async (file: File) => {
    setError(null);
    if (!(ACCEPTED as readonly string[]).includes(file.type)) {
      setError('Use a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Covers are capped at 5 MB.');
      return;
    }
    setUploading(true);
    try {
      const client = getApiClient();
      const { data, error: presignError } = await client.POST(
        '/api/v1/admin/learning-tracks/{id}/cover',
        {
          params: { path: { id: trackId } },
          body: {
            contentType: file.type as (typeof ACCEPTED)[number],
            size: file.size,
          },
        },
      );
      if (data === undefined) throwApiError(presignError);
      await uploadWithProgress({
        url: data.uploadUrl,
        body: file,
        headers: data.fields,
        onProgress: () => {},
      });
      await updateTrack.mutateAsync({
        id: trackId,
        patch: { coverImageKey: data.key },
      });
    } catch (err) {
      setError(failureFromError(err, 'Could not upload the cover.').message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeCover = async () => {
    setError(null);
    try {
      await updateTrack.mutateAsync({
        id: trackId,
        patch: { coverImageKey: null },
      });
    } catch (err) {
      setError(failureFromError(err, 'Could not remove the cover.').message);
    }
  };

  return (
    <Field
      label="Cover image"
      description="Shown on the preview tile and the top of the track page. Without one, the first resource's image is used."
    >
      <Row gap="md" align="center">
        {currentCoverUrl !== null ? (
          <img className={styles.preview} src={currentCoverUrl} alt="" />
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(',')}
          className={styles.hiddenInput}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          icon={I.image}
          onClick={pick}
          loading={uploading}
        >
          {currentCoverUrl !== null ? 'Replace image' : 'Upload image'}
        </Button>
        {currentCoverUrl !== null ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void removeCover()}
            loading={updateTrack.isPending && !uploading}
          >
            Remove
          </Button>
        ) : null}
      </Row>
      {error !== null ? (
        <Body size="sm" color="destructive">
          {error}
        </Body>
      ) : null}
    </Field>
  );
}
